import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React from 'react'
import { PrismicRichText } from '@prismicio/react'
import BertholdTitle from 'src/components/atoms/bertholdTitle'

import {
  Maybe,
  PrismicArticle,
  PrismicCategory,
  PrismicLinkType,
  PrismicStructuredTextType,
} from 'src/typings/generated/graphql'

import * as GlobalStyles from 'src/styles/global.module.scss'

import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import CustomLink from 'src/utils/customLink'
import CategoryPill from 'src/components/atoms/categoryPill'
import { generateRandomId } from 'src/utils/domHelper'
import * as Styles from './configurableCta.module.scss'

enum ConfigurableCtaLinkStyle {
  button = 'button',
  link = 'link',
}

enum ConfigurableCtaImageLayout {
  left = 'left',
  right = 'right',
}

enum TextSize {
  extraSmall = 'extra small',
  small = 'small',
  medium = 'medium',
  large = 'large',
  extraLarge = 'extra large',
}

export type ConfigurableCtaContent = {
  anchor?: string
  eyebrowText?: string
  eyebrowSize?: string
  headline?: string
  headlineSize?: string
  hasTrademark?: boolean
  description?: PrismicStructuredTextType
  descriptionSize?: string
  link?: PrismicLinkType
  linkText?: string
  linkStyle?: string
  image?: IGatsbyImageData
  imageAlt?: string
  imageLayout?: string
  mobileOnly?: boolean
  themeColor?: string
}

type Props = {
  content: ConfigurableCtaContent
}

const ConfigurableCta = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    eyebrowText = '',
    eyebrowSize = TextSize.medium,
    headline = '',
    headlineSize = TextSize.medium,
    hasTrademark = false,
    description,
    descriptionSize = TextSize.medium,
    link,
    linkText = '',
    linkStyle = ConfigurableCtaLinkStyle.button,
    image,
    imageAlt = '',
    imageLayout = ConfigurableCtaImageLayout.right,
    mobileOnly = false,
    themeColor = 'white with black text',
  } = content

  const themeColorClasses = {
    'white with black text': Styles.whiteWithBlackText,
    'black with white text': Styles.blackWithWhiteText,
    'tan with black text': Styles.tanWithBlackText,
    'seafoam 3 with black text': Styles.seafoam3WithBlackText,
    'light gray 2 with black text': Styles.lightGray2WithBlackText,
    'gold with black text': Styles.goldWithBlackText,
  }

  const eyebrowSizeClasses = {
    'extra small': Styles.eyebrowExtraSmall,
    small: Styles.eyebrowSmall,
    medium: Styles.eyebrowMedium,
    large: Styles.eyebrowLarge,
    'extra large': Styles.eyebrowExtraLarge,
  }

  const headlineSizeClasses = {
    'extra small': Styles.headlineExtraSmall,
    small: Styles.headlineSmall,
    medium: Styles.headlineMedium,
    large: Styles.headlineLarge,
    'extra large': Styles.headlineExtraLarge,
  }

  const descriptionSizeClasses = {
    'extra small': Styles.descriptionExtraSmall,
    small: Styles.descriptionSmall,
    medium: Styles.descriptionMedium,
    large: Styles.descriptionLarge,
    'extra large': Styles.descriptionExtraLarge,
  }

  const linkStyleFormatted = linkStyle.toLowerCase()
  const imageLayoutFormatted = imageLayout.toLowerCase()
  const eyebrowSizeFormatted = eyebrowSize.toLowerCase()
  const headlineSizeFormatted = headlineSize.toLowerCase()
  const descriptionSizeFormatted = descriptionSize.toLowerCase()

  let layoutClass = ''
  if (imageLayoutFormatted === ConfigurableCtaImageLayout.left)
    layoutClass = Styles.imageLeft

  // Get size classes
  const eyebrowSizeClass =
    eyebrowSizeClasses[
      eyebrowSizeFormatted as keyof typeof eyebrowSizeClasses
    ] ?? Styles.eyebrowMedium
  const headlineSizeClass =
    headlineSizeClasses[
      headlineSizeFormatted as keyof typeof headlineSizeClasses
    ] ?? Styles.headlineMedium
  const descriptionSizeClass =
    descriptionSizeClasses[
      descriptionSizeFormatted as keyof typeof descriptionSizeClasses
    ] ?? Styles.descriptionMedium

  // Render
  const renderThemeClass = () => {
    const themeColorValue =
      themeColorClasses[themeColor as keyof typeof themeColorClasses]

    return themeColorValue
  }

  const renderEyebrow = () => {
    const linkDocumentType = link?.document?.type

    if (!headline) return null

    // If link is to Article, set category as eyebrow
    if (linkDocumentType === 'article') {
      const article = link?.document as PrismicArticle
      const category = article.data?.category
        ?.document as Maybe<PrismicCategory>
      if (category)
        return (
          <div className={Styles.pillEyebrow}>
            <CategoryPill category={category.data.name ?? ''} />
          </div>
        )
    }

    if (eyebrowText)
      return (
        <p className={`${Styles.textEyebrow} ${eyebrowSizeClass}`}>
          {eyebrowText}
        </p>
      )

    return (
      <div className={Styles.eyebrow}>
        <Eyebrow />
      </div>
    )
  }

  const renderLink = () => {
    if (!link?.url) return null

    const tempLink = link as PrismicLinkType

    const basicLinkType =
      themeColor === 'black with white text'
        ? GlobalStyles.whiteBasicLink
        : GlobalStyles.redBasicLink
    const responsiveLinkType =
      themeColor === 'black with white text'
        ? GlobalStyles.whiteButtonResponsive
        : GlobalStyles.redButtonResponsive

    const styleClass =
      linkStyleFormatted === ConfigurableCtaLinkStyle.link
        ? basicLinkType
        : responsiveLinkType

    return (
      <CustomLink link={tempLink} className={styleClass}>
        {linkText ?? ''}
      </CustomLink>
    )
  }

  let sliceClass = `${Styles.copyImageCtaSlice} ${layoutClass}`
  if (mobileOnly) {
    sliceClass += ` ${Styles.mobileOnly}`
  }

  // Determine if image is decorative (no alt text)
  const isDecorativeImage = !imageAlt

  return (
    <section
      id={anchor}
      className={`${sliceClass} ${renderThemeClass()}`}
      aria-label={headline || undefined}
    >
      {!!image && (
        <div className={Styles.imageWrapper}>
          <GatsbyImage
            image={image}
            alt={imageAlt ?? ''}
            className={Styles.image}
            {...(isDecorativeImage && { 'aria-hidden': true })}
          />
        </div>
      )}
      <div className={Styles.contentWrapper}>
        <div className={Styles.content}>
          {renderEyebrow()}
          <h2 className={headlineSizeClass}>
            {BertholdTitle({ text: headline })}
            {hasTrademark && <sup>®</sup>}
          </h2>
          <div className={descriptionSizeClass}>
            <PrismicRichText
              field={description?.richText}
              components={{
                hyperlink: ({ node, children }: any) =>
                  CustomLink({ link: node.data, children }),
              }}
            />
          </div>
          <div className={Styles.link}>{renderLink()}</div>
        </div>
      </div>
    </section>
  )
}

export default ConfigurableCta
